import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/brand/logo'

type CoverTone = 'ink' | 'blue' | 'mist'

interface CoverArtProps {
    seed: string
    label?: string
    className?: string
    tone?: CoverTone
}

interface Palette {
    background: string
    deep: string
    line: string
    gridOpacity: number
    stroke: string
    accent: string
    glow: string
    gold: string
    dark: boolean
}

interface Composition {
    random: () => number
    palette: Palette
}

const palettes: Record<CoverTone, Palette> = {
    ink: { background: '#0d1526', deep: '#070c18', line: '#ffffff', gridOpacity: 0.07, stroke: '#ffffff', accent: '#2fd0e4', glow: '#2448b5', gold: '#c9a24d', dark: true },
    blue: { background: '#2448b5', deep: '#13286f', line: '#ffffff', gridOpacity: 0.1, stroke: '#ffffff', accent: '#2fd0e4', glow: '#2fd0e4', gold: '#c9a24d', dark: true },
    mist: { background: '#f5f7fb', deep: '#e3e9f4', line: '#0d1526', gridOpacity: 0.07, stroke: '#0d1526', accent: '#2448b5', glow: '#2fd0e4', gold: '#c9a24d', dark: false }
}

const TONES: Array<CoverTone> = ['ink', 'blue', 'mist']
const WIDTH = 800
const HEIGHT = 500

/** FNV-1a: a small, stable hash of the seed string. */
const hashSeed = (value: string): number => {
    let hash = 2166136261

    for (let index = 0; index < value.length; index++) {
        hash ^= value.charCodeAt(index)
        hash = Math.imul(hash, 16777619)
    }

    return hash >>> 0
}

/** Mulberry32: a deterministic sequence of numbers in [0, 1) from the hash, identical on server and client. */
const sequence = (seed: number): (() => number) => {
    let state = seed || 1

    return (): number => {
        state = (state + 0x6d2b79f5) | 0
        let value = Math.imul(state ^ (state >>> 15), 1 | state)
        value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value
        return ((value ^ (value >>> 14)) >>> 0) / 4294967296
    }
}

const round = (value: number): number => Math.round(value * 10) / 10

const between = (random: () => number, min: number, max: number): number => round(min + random() * (max - min))

/** A suspension bridge: towers, a parabolic main cable with hangers, the deck and its reflection. */
const drawBridge = ({ random, palette }: Composition): ReactNode => {
    const deck = between(random, 300, 350)
    const left = between(random, 160, 230)
    const right = between(random, 570, 640)
    const top = between(random, 105, 145)
    const low = round(deck - between(random, 28, 60))
    const middle = round((left + right) / 2)
    const control = round(2 * low - top)
    const hangers = 9 + Math.floor(random() * 6)

    const points = Array.from({ length: hangers }, (_, index) => {
        const t = (index + 1) / (hangers + 1)
        const x = round(left + t * (right - left))
        const y = round((1 - t) * (1 - t) * top + 2 * (1 - t) * t * control + t * t * top)
        return { x, y }
    })

    const ripples = Array.from({ length: 4 }, (_, index) => ({
        y: round(deck + 46 + index * 24),
        x: between(random, 60, 420),
        length: between(random, 120, 320),
        opacity: round(0.22 - index * 0.045)
    }))

    return (
        <g fill='none' strokeLinecap='round'>
            <path d={`M${left} ${top} Q${middle} ${control} ${right} ${top}`} stroke={palette.stroke} strokeWidth='2.2' strokeOpacity='0.85' />
            <path d={`M${left} ${top} Q${round(left - 70)} ${round(deck - 24)} 16 ${round(deck - 4)}`} stroke={palette.stroke} strokeWidth='1.6' strokeOpacity='0.5' />
            <path d={`M${right} ${top} Q${round(right + 70)} ${round(deck - 24)} ${WIDTH - 16} ${round(deck - 4)}`} stroke={palette.stroke} strokeWidth='1.6' strokeOpacity='0.5' />

            {points.map((point, index) => (
                <line key={index} x1={point.x} y1={point.y} x2={point.x} y2={deck} stroke={palette.stroke} strokeWidth='1' strokeOpacity='0.4' />
            ))}

            <line x1={left} y1={round(top - 14)} x2={left} y2={round(deck + 78)} stroke={palette.stroke} strokeWidth='4' strokeOpacity='0.9' />
            <line x1={right} y1={round(top - 14)} x2={right} y2={round(deck + 78)} stroke={palette.stroke} strokeWidth='4' strokeOpacity='0.9' />
            <line x1={round(left - 12)} y1={round(top + 34)} x2={round(left + 12)} y2={round(top + 34)} stroke={palette.stroke} strokeWidth='2' strokeOpacity='0.7' />
            <line x1={round(right - 12)} y1={round(top + 34)} x2={round(right + 12)} y2={round(top + 34)} stroke={palette.stroke} strokeWidth='2' strokeOpacity='0.7' />

            <line x1='0' y1={deck} x2={WIDTH} y2={deck} stroke={palette.stroke} strokeWidth='2.6' />
            <line x1='0' y1={round(deck + 9)} x2={WIDTH} y2={round(deck + 9)} stroke={palette.stroke} strokeWidth='1' strokeOpacity='0.35' />

            {ripples.map((ripple, index) => (
                <line key={index} x1={ripple.x} y1={ripple.y} x2={round(ripple.x + ripple.length)} y2={ripple.y} stroke={palette.accent} strokeWidth='1.2' strokeOpacity={ripple.opacity} />
            ))}

            <circle cx={middle} cy={low} r='16' stroke={palette.accent} strokeWidth='1.2' strokeOpacity='0.5' />
            <circle cx={middle} cy={low} r='5.5' fill={palette.accent} stroke='none' />
            <circle cx={left} cy={round(top - 14)} r='4' fill={palette.gold} stroke='none' />
        </g>
    )
}

/** Rising data bars under an arch, with a trend line and nodes. */
const drawBars = ({ random, palette }: Composition): ReactNode => {
    const base = 420
    const count = 8 + Math.floor(random() * 4)
    const gap = 600 / count
    const width = round(Math.min(34, gap * 0.48))
    const highlight = Math.floor(random() * count)

    const bars = Array.from({ length: count }, (_, index) => {
        const height = round(50 + (index / count) * 150 + random() * 80)
        const x = round(100 + index * gap)
        return { x, height, center: round(x + width / 2), top: round(base - height) }
    })

    const trend = bars.map(bar => `${bar.center},${round(bar.top - 26)}`).join(' ')
    const last = bars[bars.length - 1]

    return (
        <g strokeLinecap='round'>
            <path d={`M60 ${base} A340 300 0 0 1 740 ${base}`} fill='none' stroke={palette.stroke} strokeWidth='1.5' strokeOpacity='0.4' />
            <path d={`M150 ${base} A250 210 0 0 1 650 ${base}`} fill='none' stroke={palette.stroke} strokeWidth='1' strokeOpacity='0.25' strokeDasharray='3 7' />

            {bars.map((bar, index) => (
                <rect
                    key={index}
                    x={bar.x}
                    y={bar.top}
                    width={width}
                    height={bar.height}
                    rx='3'
                    fill={index === highlight ? palette.accent : palette.stroke}
                    fillOpacity={index === highlight ? 0.9 : 0.16}
                />
            ))}

            <line x1='40' y1={base} x2={WIDTH - 40} y2={base} stroke={palette.stroke} strokeWidth='2' />
            <polyline points={trend} fill='none' stroke={palette.stroke} strokeWidth='1.8' strokeOpacity='0.85' strokeLinejoin='round' />

            {bars.map((bar, index) => (
                <circle key={index} cx={bar.center} cy={round(bar.top - 26)} r='3.5' fill={index === highlight ? palette.gold : palette.stroke} />
            ))}

            {last && (
                <>
                    <circle cx={last.center} cy={round(last.top - 26)} r='14' fill='none' stroke={palette.accent} strokeWidth='1.2' strokeOpacity='0.6' />
                    <circle cx={last.center} cy={round(last.top - 26)} r='5.5' fill={palette.accent} />
                </>
            )}
        </g>
    )
}

/** A network of nodes on the grid, joined by a bridging arc, with one hub. */
const drawNetwork = ({ random, palette }: Composition): ReactNode => {
    const target = 7 + Math.floor(random() * 4)
    const taken = new Set<string>()
    const nodes: Array<{ x: number, y: number }> = []

    for (let attempt = 0; attempt < 40 && nodes.length < target; attempt++) {
        const column = Math.floor(random() * 9)
        const row = Math.floor(random() * 5)
        const key = `${column}-${row}`
        if (taken.has(key)) continue
        taken.add(key)
        nodes.push({ x: 80 + column * 80, y: 90 + row * 80 })
    }

    nodes.sort((a, b) => a.x - b.x || a.y - b.y)

    const hub = nodes[Math.floor(random() * nodes.length)]
    const gold = nodes[Math.floor(random() * nodes.length)]
    const first = nodes[0]
    const last = nodes[nodes.length - 1]
    const highest = Math.min(...nodes.map(node => node.y))

    return (
        <g strokeLinecap='round'>
            {nodes.slice(2).map((node, index) => (
                <line key={`skip-${index}`} x1={nodes[index].x} y1={nodes[index].y} x2={node.x} y2={node.y} stroke={palette.stroke} strokeWidth='1' strokeOpacity='0.2' />
            ))}

            {nodes.slice(1).map((node, index) => (
                <line key={`link-${index}`} x1={nodes[index].x} y1={nodes[index].y} x2={node.x} y2={node.y} stroke={palette.stroke} strokeWidth='1.6' strokeOpacity='0.7' />
            ))}

            {first && last && (
                <path
                    d={`M${first.x} ${first.y} Q${round((first.x + last.x) / 2)} ${round(highest - 150)} ${last.x} ${last.y}`}
                    fill='none'
                    stroke={palette.accent}
                    strokeWidth='1.6'
                    strokeOpacity='0.75'
                    strokeDasharray='4 7'
                />
            )}

            {hub && (
                <>
                    <circle cx={hub.x} cy={hub.y} r='48' fill='none' stroke={palette.accent} strokeWidth='1' strokeOpacity='0.2' />
                    <circle cx={hub.x} cy={hub.y} r='26' fill='none' stroke={palette.accent} strokeWidth='1.2' strokeOpacity='0.45' />
                </>
            )}

            {nodes.map((node, index) => (
                <circle key={index} cx={node.x} cy={node.y} r={node === hub ? 7 : 4} fill={node === hub ? palette.accent : node === gold ? palette.gold : palette.stroke} />
            ))}
        </g>
    )
}

/** Concentric arches rising from a common base, crossed by a deck. */
const drawArches = ({ random, palette }: Composition): ReactNode => {
    const centerX = between(random, 260, 540)
    const baseY = 450
    const count = 5 + Math.floor(random() * 3)
    const step = 50 + Math.floor(random() * 16)
    const highlight = 1 + Math.floor(random() * (count - 2))
    const deck = round(baseY - between(random, 70, 150))

    const radii = Array.from({ length: count }, (_, index) => 60 + index * step)
    const accentRadius = radii[highlight] ?? radii[0]

    return (
        <g fill='none' strokeLinecap='round'>
            {radii.map((radius, index) => (
                <path
                    key={index}
                    d={`M${round(centerX - radius)} ${baseY} A${radius} ${radius} 0 0 1 ${round(centerX + radius)} ${baseY}`}
                    stroke={index === highlight ? palette.accent : palette.stroke}
                    strokeWidth={index === highlight ? 2.6 : 1.3}
                    strokeOpacity={index === highlight ? 0.95 : round(0.55 - index * 0.06)}
                />
            ))}

            <line x1='0' y1={deck} x2={WIDTH} y2={deck} stroke={palette.stroke} strokeWidth='2.4' />
            <line x1='0' y1={baseY} x2={WIDTH} y2={baseY} stroke={palette.stroke} strokeWidth='1' strokeOpacity='0.4' />

            {radii.map((radius, index) => (
                <circle key={`end-${index}`} cx={round(centerX + radius)} cy={baseY} r='3' fill={palette.stroke} fillOpacity='0.7' stroke='none' />
            ))}

            <circle cx={centerX} cy={round(baseY - accentRadius)} r='5.5' fill={palette.gold} stroke='none' />
            <circle cx={centerX} cy={round(baseY - accentRadius)} r='15' stroke={palette.gold} strokeWidth='1' strokeOpacity='0.5' />
        </g>
    )
}

const compositions: Array<(composition: Composition) => ReactNode> = [drawBridge, drawBars, drawNetwork, drawArches]

/**
 * Deterministic abstract cover art for content without a photo. The composition,
 * tone and glow are chosen from a hash of the seed (use the slug), so the same
 * article always renders the same image on the server and in the browser.
 * The label is static UI copy only (for example the content type).
 */
export const CoverArt: FC<CoverArtProps> = ({ seed, label, className, tone }): ReactNode => {
    const hash = hashSeed(seed)
    const random = sequence(hash)
    const chosenTone = tone ?? TONES[hash % TONES.length]
    const palette = palettes[chosenTone]
    const draw = compositions[Math.floor(hash / 7) % compositions.length]
    const uid = `cover-${hash.toString(36)}-${chosenTone}`
    const glowX = between(random, 120, 680)
    const glowY = between(random, 60, 320)
    const art = draw({ random, palette })

    const columns = Array.from({ length: WIDTH / 40 - 1 }, (_, index) => (index + 1) * 40)
    const rows = Array.from({ length: Math.floor(HEIGHT / 40) }, (_, index) => (index + 1) * 40)

    return (
        <div className={cn('relative overflow-hidden', palette.dark ? 'bg-ink' : 'bg-mist', className)}>
            <svg
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                preserveAspectRatio='xMidYMid slice'
                className='block size-full'
                aria-hidden='true'
                focusable='false'
            >
                <defs>
                    <linearGradient id={`${uid}-bg`} x1='0' y1='0' x2='1' y2='1'>
                        <stop offset='0' stopColor={palette.background} />
                        <stop offset='1' stopColor={palette.deep} />
                    </linearGradient>

                    <radialGradient id={`${uid}-glow`} cx={glowX} cy={glowY} r='340' gradientUnits='userSpaceOnUse'>
                        <stop offset='0' stopColor={palette.glow} stopOpacity={palette.dark ? 0.55 : 0.35} />
                        <stop offset='1' stopColor={palette.glow} stopOpacity='0' />
                    </radialGradient>
                </defs>

                <rect width={WIDTH} height={HEIGHT} fill={`url(#${uid}-bg)`} />
                <rect width={WIDTH} height={HEIGHT} fill={`url(#${uid}-glow)`} />

                <g stroke={palette.line} strokeWidth='1'>
                    {columns.map(x => (
                        <line key={`x-${x}`} x1={x} y1='0' x2={x} y2={HEIGHT} strokeOpacity={x % 160 === 0 ? palette.gridOpacity * 1.8 : palette.gridOpacity} />
                    ))}

                    {rows.map(y => (
                        <line key={`y-${y}`} x1='0' y1={y} x2={WIDTH} y2={y} strokeOpacity={y % 160 === 0 ? palette.gridOpacity * 1.8 : palette.gridOpacity} />
                    ))}
                </g>

                {art}
            </svg>

            {label && (
                <span
                    className={cn(
                        'absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.18em] backdrop-blur-sm',
                        palette.dark ? 'border-white/15 bg-white/5 text-white/80' : 'border-ink/10 bg-white/60 text-ink/70'
                    )}
                >
                    <span className={cn('size-1.5 rounded-full', palette.dark ? 'bg-signal' : 'bg-primary')} aria-hidden='true' />
                    {label}
                </span>
            )}

            <LogoMark tone={palette.dark ? 'light' : 'dark'} className='absolute bottom-4 right-4 size-6 opacity-60' />
        </div>
    )
}
